/**
 * SubscriptionBreakdown — Phase 43.3
 *
 * Mostra il dettaglio trasparente di cosa il cliente paga oggi quando la
 * consegna è inclusa nell'abbonamento. Evita confusione e contestazioni
 * facendo capire che l'abbonamento copre la consegna base, ma i supplementi
 * speciali (weekend, sera/notte, extra km, Express) sono sempre a carico
 * del cliente.
 *
 * Props accettati: oggetto pricing/order con i campi:
 *   subscription_credit_applied: float
 *   weekend_surcharge: float
 *   weekend_surcharge_pct: int
 *   time_slot_surcharge: float
 *   time_slot_name: string ("Sera" | "Notte" | ...)
 *   time_slot_multiplier: float
 *   extra_km_charge: float
 *   included_km_per_delivery: int
 *   express_supplement: float
 *   final_price: float  (o customer_charge)
 *   included_remaining: int
 *   included_delivery_consumed: boolean
 *
 * variant: "card" (default, riepilogo checkout) | "inline" (dettaglio ordine).
 */
import { Check, Plus } from "lucide-react";
import { formatCHF } from "@/lib/format";
import { validPriceTax } from "@/lib/priceTax";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";

export default function SubscriptionBreakdown({ data, variant = "card", className = "" }) {
  if (!data || !data.included_delivery_consumed) return null;

  const weekend = Number(data.weekend_surcharge || 0);
  const slotSurcharge = Number(data.time_slot_surcharge || 0);
  const extraKm = Number(data.extra_km_charge || 0);
  const expressSup = Number(data.express_supplement || 0);
  const total = Number(data.funding_preview?.amount_due_chf ?? data.final_price ?? data.customer_charge ?? 0);
  const tax = validPriceTax(data.charge_tax) ? data.charge_tax : null;
  const fullTax = validPriceTax(data.price_tax) ? data.price_tax : null;
  const business = tax?.audience === "business";
  const netCreditKnown = business && fullTax?.audience === "business"
    && fullTax.vat_rate === tax.vat_rate && fullTax.net_chf >= tax.net_chf;
  const credit = netCreditKnown
    ? (Math.round(fullTax.net_chf * 100) - Math.round(tax.net_chf * 100)) / 100
    : Number(data.subscription_credit_applied || 0);
  const wallet = data.funding_preview?.wallet_credit_apply_chf ?? data.credit_balance_applied_chf ?? 0;
  const includedKm = data.included_km_per_delivery || 10;
  const slotName = data.time_slot_name || "";
  const slotPct = data.time_slot_multiplier
    ? Math.round((Number(data.time_slot_multiplier) - 1) * 100)
    : 0;
  const wePct = data.weekend_surcharge_pct || 0;

  // Se non c'è alcun supplemento (caso felice) -> messaggio "tutto incluso"
  const hasAnySurcharge = weekend > 0 || slotSurcharge > 0 || extraKm > 0 || expressSup > 0;

  const container =
    variant === "inline"
      ? "rounded-2xl bg-dapnow-bg p-4 space-y-1.5 text-sm"
      : "rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 sm:p-5 space-y-2";

  return (
    <div className={`${container} ${className}`} data-testid="subscription-breakdown">
      <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-800">
        Cosa paghi oggi
      </p>
      {business && <p className="text-xs text-emerald-800">I supplementi qui sotto sono IVA esclusa. L’IVA è indicata nel riepilogo.</p>}

      {credit > 0 && (
        <BdRow
          testid="bd-row-credit"
          icon={<Check className="h-3.5 w-3.5 text-emerald-700" />}
          label={`Consegna inclusa nel piano${netCreditKnown ? " (IVA esclusa)" : business ? " (importo registrato)" : ""}`}
          value={`− ${formatCHF(credit)}`}
          tone="credit"
        />
      )}

      {slotSurcharge > 0 && (
        <BdRow
          testid="bd-row-time-slot"
          icon={<Plus className="h-3.5 w-3.5 text-amber-700" />}
          label={`Supplemento ${slotName.toLowerCase() || "fascia oraria"}${slotPct ? ` (+${slotPct}%)` : ""}`}
          value={`+ ${formatCHF(slotSurcharge)}`}
          tone="extra"
        />
      )}

      {weekend > 0 && (
        <BdRow
          testid="bd-row-weekend"
          icon={<Plus className="h-3.5 w-3.5 text-amber-700" />}
          label={`Supplemento weekend / festivi${wePct ? ` (+${wePct}%)` : ""}`}
          value={`+ ${formatCHF(weekend)}`}
          tone="extra"
        />
      )}

      {extraKm > 0 && (
        <BdRow
          testid="bd-row-extra-km"
          icon={<Plus className="h-3.5 w-3.5 text-amber-700" />}
          label={`Extra km fuori piano (oltre ${includedKm} km)`}
          value={`+ ${formatCHF(extraKm)}`}
          tone="extra"
        />
      )}

      {expressSup > 0 && (
        <BdRow
          testid="bd-row-express"
          icon={<Plus className="h-3.5 w-3.5 text-amber-700" />}
          label="Supplemento Express"
          value={`+ ${formatCHF(expressSup)}`}
          tone="extra"
        />
      )}
      {tax && <CustomerTaxBreakdown tax={tax} testId="subscription-charge-tax" totalLabel="Totale prima del wallet" />}
      {tax && wallet > 0 && <BdRow testid="bd-row-wallet" label="Credito wallet utilizzato" value={`− ${formatCHF(wallet)}`} tone="credit" />}

      <div
        className="flex items-center justify-between pt-2 mt-2 border-t border-emerald-200"
        data-testid="bd-row-total"
      >
        <span className="font-display text-base font-bold tracking-tight">
          Totale da pagare oggi
        </span>
        <span className="font-display text-lg font-extrabold tracking-tighter">
          {formatCHF(total)}
        </span>
      </div>

      {!hasAnySurcharge && total === 0 && (
        <p className="text-[11px] text-emerald-800 leading-snug">
          Niente da pagare oggi: questa consegna rientra interamente nel tuo piano.
        </p>
      )}
      {data.included_remaining != null && (
        <p className="text-[11px] text-dapnow-muted">
          Restano <strong>{data.included_remaining}</strong> consegne incluse questo mese.
        </p>
      )}
    </div>
  );
}

function BdRow({ icon, label, value, tone, testid }) {
  const valueClass =
    tone === "credit" ? "text-emerald-700 font-bold" : "text-amber-800 font-semibold";
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className="inline-flex items-center gap-1.5 text-dapnow-ink">
        {icon}
        {label}
      </span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
